import { ActionFunctionArgs, LoaderFunction } from 'react-router-dom';
import useSidebarStore from '@/stores/sidebarStore';
import { useDashboardSidebarStore } from '@/stores/dashboardsSidebarStore';
import { Navbar } from '../Navbar/Navbar';
import { sleep } from '@/utils/helpers';
import { showToast } from '@/utils/toasts';
import { FirestoreError } from 'firebase/firestore';
import { firebaseFunctionErrorsMapping } from '@/utils/constants';
import { WorkspacesService } from '@/services/workspacesService';
import { OrganizationsService } from '@/services/organizationsService';
import { IndexDB } from '@/utils/indexDB';
import { useWorkspaceData } from './hooks/useWorkspaceData';
import { useWorkspaceModals } from './hooks/useWorkspaceModals';
import { WorkspaceSidebar } from './components/WorkspaceSidebar';
import { WorkspaceContent } from './components/WorkspaceContent';
import { WorkspaceModals } from './components/WorkspaceModals';

export const WorkspaceLayout = () => {
  const {
    workspaceId,
    workspaces,
    selectedOrganization,
    localSelectedOrganization,
    setLocalSelectedOrganization,
    localOrganizationPermission,
    invitations,
    organizationInvitations,
    isEmailVerified,
    setNewlyCreatedOrganizationAsLocalSelectedOrganization,
  } = useWorkspaceData();

  const { isOpen, dispatch, handleCloseModal, handleOpenNewWorkspaceModal, closeModal } =
    useWorkspaceModals(setNewlyCreatedOrganizationAsLocalSelectedOrganization);

  const isDataforSeo = useDashboardSidebarStore((state) => state.isDataforseo);
  const isExpanded = useSidebarStore((state) => state.isOpen);

  const selectedWorkspaceName = workspaces.find(
    (workspace) => workspace.id === workspaceId,
  )?.name;

  const url = new URL(window.location.href);
  let shouldDisplaySidebar = !!workspaceId;
  let shouldDisplaySwitchEntityButton = true;
  if (!isEmailVerified) {
    shouldDisplaySidebar = false;
    shouldDisplaySwitchEntityButton = false;
  } else if (url.pathname === '/handleInvitation' || url.pathname === '/subscriptions') {
    shouldDisplaySidebar = false;
  }
  const invitationsAmount = invitations.length + organizationInvitations.length;

  const handleOpenSwitchEntityModal = () => {
    dispatch({ type: 'OPEN', payload: 'switchWorkspaceModal' });
  };

  return (
    <>
      <Navbar
        invitationsAmount={invitationsAmount}
        openSwitchEntityModal={
          shouldDisplaySwitchEntityButton ? handleOpenSwitchEntityModal : undefined
        }
        selectedOrganizationName={
          shouldDisplaySwitchEntityButton
            ? (selectedOrganization?.name as string)
            : undefined
        }
        selectedWorkspaceName={
          shouldDisplaySwitchEntityButton ? (selectedWorkspaceName as string) : undefined
        }
      />
      <section style={{ display: 'flex', flex: '1' }}>
        {shouldDisplaySidebar ? (
          <WorkspaceSidebar
            isExpanded={isExpanded}
            isDataforSeo={isDataforSeo}
            workspaceId={workspaceId}
            hasSelectedOrganization={!!selectedOrganization}
          />
        ) : null}
        <WorkspaceContent shouldDisplaySidebar={shouldDisplaySidebar} />
      </section>
      <WorkspaceModals
        isOpen={isOpen}
        localSelectedOrganization={localSelectedOrganization}
        setLocalSelectedOrganization={setLocalSelectedOrganization}
        handleOpenNewWorkspaceModal={handleOpenNewWorkspaceModal}
        handleCloseModal={handleCloseModal}
        closeModal={closeModal}
        localOrganizationPermission={localOrganizationPermission}
      />
    </>
  );
};

export async function createEntityAction({ request }: ActionFunctionArgs) {
  const data = await request.formData();
  const entityName = data.get('entityName') as string;
  const entityType = data.get('entityType') as string;
  if (entityType === 'organizations') {
    const region = data.get('region') as string;
    const url = new URL(request.url);
    const priceId = url.searchParams.get('priceId') as string;
    try {
      const res = await OrganizationsService.createOrganization(
        entityName,
        region,
        priceId,
      );
      await sleep(1000); // Wait for the organization to be created
      return { organizationId: res.data };
    } catch (error) {
      showToast('Error!', 'Failed to create an organization. Try again later!', 'error');
      return { error };
    }
  }
  const organizationId = data.get('organizationId') as string;
  try {
    const res = await WorkspacesService.createWorkspace(entityName, organizationId);
    await sleep(1000); // Wait for the workspace to be created
    return { workspaceId: res.data };
  } catch (e) {
    const error = e as FirestoreError;
    showToast(
      'Error!',
      firebaseFunctionErrorsMapping[error.message] ||
        'Failed to create a workspace. Try again later!',
      'error',
    );
    return { error };
  }
}

export const userIdLoader: LoaderFunction = async () => {
  const userId = await IndexDB.getUserId();
  if (!userId) throw { code: 'no-user-id', message: 'No user id provided' };

  return { id: userId };
};
